import { useEffect, useRef, useState } from "react";
import { formatFileSize } from "../../Utils/formatSize";
import { Link } from "react-router-dom";
import linkifyHtml from "linkify-html";
import DOMPurify from "dompurify";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../Utils/firebase";
import Chat from "../../Pages/Chat";
import ChatVideoPlayer from "./ChatVideoPlayer";
import ChatAudioPlayer from "./ChatAudioPlayer";
import ChatEmbedMedia from "./ChatEmbedMedia.jsx";
import JournalDetailModal from "../Modals/JournalDetailModal";
import parse from "html-react-parser";
import {
  extractHostedVideoEmbeds,
  parseLinkifiedHtmlWithVideoEmbeds,
} from "../../Utils/parseLinkifiedHtmlWithVideoEmbeds";

const ReceiveChat = ({
  response,
  responder,
  time,
  userImage,
  isFile,
  fileInfo,
  fileType,
  isUploading,
  uploadProgress,
  onDownload,
  messageId,
  isEditing,
  disableEdit,
  isLiked,
  conversationId,
  replyCount,
  userId,
  journal = {},
  onLike = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onReply = () => {},
  onSaveEdit = () => {},
  onCancelEdit = () => {},
  showReply = true,
  userReaction,
  reactionSummary,
  replyMessage,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const reactions = [
    "👍",
    "❤️",
    "😂",
    "😮",
    "😢",
    "👎",
    "🔥",
    "✅",
    "🚀",
    "🙏",
    "💯",
  ];
  const [selectedReaction, setSelectedReaction] = useState(null);
  const [showReactions, setShowReactions] = useState(false);
  const reactionsRef = useRef(null);
  const [showJournalModal, setShowJournalModal] = useState(false); // Retained

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        reactionsRef.current &&
        !reactionsRef.current.contains(event.target)
      ) {
        setShowReactions(false);
      }
    }

    if (showReactions) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showReactions]);

  // Check if message is a shared journal
  const isJournalShared =
    journal?.isJournalShared &&
    journal?.journal_keywords &&
    journal?.user_journal_id &&
    journal?.emotion_name;

  // Format keywords for display
  const formatKeywords = (keywords) => {
    if (!keywords || !Array.isArray(keywords)) return "";
    return keywords
      .map((keyword) => keyword.charAt(0).toUpperCase() + keyword.slice(1))
      .join(", ");
  };

  // Handle journal click
  const handleJournalClick = () => {
    setShowJournalModal(true);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    setShowReactions((prev) => !prev);
  };

  const handleReactionSelect = (emoji) => {
    setSelectedReaction(emoji);
    setShowReactions(false); // hide after selecting
    onLike(messageId, emoji);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(messageId, response ?? "");
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    onDelete(messageId);
    setShowDeleteModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleReply = (e) => {
    e.stopPropagation();
    onReply({
      id: messageId,
      content: response,
      conversationId: conversationId,
      userImage: userImage,
    });
  };

  useEffect(() => {
    if (!userId) return;

    const fetchUserData = async () => {
      try {
        const userRef = doc(db, "users", String(userId));
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setUserData(userSnap.data());
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    fetchUserData();
  }, [userId]);

  // const parseMentions = (text) => {
  //   if (!text) return "";
  //   return text.replace(/(@\w+)/g, '<span class="receive-mention">$1</span>');
  // };

  const html = linkifyHtml(DOMPurify.sanitize(response ?? ""), {
    target: "_blank",
    rel: "noopener noreferrer",
    attributes: {
      style: "color: white;",
    },
  });

  const replyHtml = linkifyHtml(
    DOMPurify.sanitize(replyMessage?.message ?? ""),
    {
      target: "_blank",
      rel: "noopener noreferrer",
      attributes: {
        style: "color: white;",
      },
    },
  );

  const hasMeaningfulHtml = (value = "") => {
    const plainText = value
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .trim();
    return plainText.length > 0;
  };

  const hasCaptionText = hasMeaningfulHtml(response);

  // Pull any pasted hosted-video links (YouTube / Vimeo / Loom / IG / FB) out of
  // the message HTML so they render as standalone players (matching the
  // uploaded-video UI) instead of sitting inside the blue .my-text-chat bubble.
  const {
    videoUrls: hostedVideoUrls,
    linkUrls: sharedLinkUrls,
    textHtml: cleanedTextHtml,
    hasText: hasCleanedText,
  } = extractHostedVideoEmbeds(html, { keepLinkAnchorsInline: true });
  const hasHostedVideos = hostedVideoUrls.length > 0;
  const hasSharedLinks = sharedLinkUrls.length > 0;
  const hasStandaloneEmbeds = hasHostedVideos || hasSharedLinks;

  const renderChatEmbeds = () => (
    <ChatEmbedMedia videoUrls={hostedVideoUrls} linkUrls={sharedLinkUrls} />
  );

  return (
    <>
      <div
        id="journal-response2"
        className={`journal-message-for-bot ${isLiked ? "liked" : ""} ${
          isEditing ? "editing" : ""
        }`}
        style={{ display: "flex", cursor: "pointer" }}
        // onClick={() => setShowActions((prev) => !prev)}
      >
        <div className="d-flex flex-column">
          <div
            className="message-edit-box"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
            }}
          >
            <div className="goals-chat-header-small d-flex align-items-center gap-4 messager-head">
              <span className="text-caption-c2" style={{ color: "#d0d8df" }}>
                {time}
              </span>
              <span className="text-body-b2" style={{ color: "#e6ecf1" }}>
                {/* Use fresh data from users collection if available, otherwise use prop */}
                {userData?.name || userData?.full_name || responder}
              </span>
            </div>
            <div className="message-actions-wrapper my-text-chat">
              {isFile ? (
                // <div className="file-message">
                <>
                  {isUploading ? (
                    <div className="upload-progress">
                      <div
                        className="progress-bar"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                      <span>Uploading {uploadProgress}%</span>
                    </div>
                  ) : fileType === "image" && fileInfo.url ? (
                    <>
                      <div
                        className="message-with-like-container"
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: "8px",
                          flexWrap: "wrap",
                          position: "relative",
                          flexDirection: "column",
                        }}
                      >
                        <div className="image-preview">
                          <img
                            src={fileInfo.url}
                            alt={fileInfo.name || "file preview"}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              borderRadius: "8px",
                              objectFit: "contain",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              onDownload();
                            }}
                          />
                        </div>
                        {hasCaptionText && (
                          <div
                            className="message-with-like-container"
                            style={{
                              display: "flex",
                              alignItems: "flex-end",
                              gap: "8px",
                              flexFlow: "column wrap",
                              position: "relative",
                            }}
                          >
                            {hasStandaloneEmbeds && renderChatEmbeds()}
                            {hasCleanedText && (
                              <div
                                className="my-text-chat mb-0 new-message-content message-content position-relative text-body-b2"
                                style={{
                                  wordBreak: "break-word",
                                  width: "fit-content",
                                  alignSelf: "flex-end",
                                }}
                              >
                                {parseLinkifiedHtmlWithVideoEmbeds(
                                  cleanedTextHtml,
                                  { keepLinkAnchorsInline: true },
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {isLiked && !isEditing && (
                          <div
                            className="like-emoji-container mt-1"
                            style={{
                              textAlign: "right",
                              left: "14px",
                              bottom: "-14px",
                              position: "absolute",
                            }}
                          >
                            <span
                              className="like-emoji"
                              style={{
                                fontSize: "18px",
                                display: "inline-block",
                                visibility: "visible",
                                opacity: 1,
                                zIndex: 0,
                                position: "relative",
                                color: "#FFD700",
                                lineHeight: 1,
                              }}
                            >
                              👍
                            </span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="message-with-like-container"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          flexWrap: "wrap",
                          position: "relative",
                          flexDirection: "column",
                        }}
                      >
                        {/* <div
                          className="file-preview"
                          style={{
                            flexDirection: "column",
                            alignItems: "start",
                          }}
                        > */}
                        <div className="file-icon d-flex align-items-end flex-column">
                          {fileType === "video" && (
                            <span>
                              <ChatVideoPlayer videoUrl={fileInfo.url} />
                            </span>
                          )}
                          {fileType === "audio" && (
                            <span>
                              <ChatAudioPlayer src={fileInfo.url} />
                            </span>
                          )}
                          {fileType === "document" && (
                            <a
                              href={fileInfo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#b3ddff" }}
                              className="text-decoration-none"
                            >
                              <span>
                                {/* <span style={{ fontSize: "20px" }}>📄</span> */}
                                📄
                              </span>
                            </a>
                          )}
                          {/* </div> */}
                          <div className="file-meta">
                            <a
                              href={fileInfo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault();
                                onDownload();
                              }}
                              style={{ color: "#b3ddff" }}
                              className="text-decoration-none"
                            >
                              {/* <span style={{ fontSize: "20px" }}>📄</span> */}
                            </a>
                            {hasCaptionText && (
                              <>
                                {hasStandaloneEmbeds && (
                                  <div className="mt-2">
                                    {renderChatEmbeds()}
                                  </div>
                                )}
                                {hasCleanedText && (
                                  <div
                                    className="my-text-chat mb-0 new-message-content message-content position-relative text-body-b2 mt-2"
                                    style={{
                                      wordBreak: "break-word",
                                      width: "fit-content",
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {parseLinkifiedHtmlWithVideoEmbeds(
                                      cleanedTextHtml,
                                      { keepLinkAnchorsInline: true },
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {replyCount > 0 && (
                            <Link
                              to={`/threads?conversationId=${
                                conversationId + "_" + messageId
                              }&messageId=${messageId}`}
                              className="reply-link"
                            >
                              {replyCount} Replies
                            </Link>
                          )}
                        </div>
                        {isLiked && !isEditing && (
                          <div
                            className="like-emoji-container mt-1"
                            style={{
                              textAlign: "right",
                              left: "14px",
                              bottom: "-14px",
                              position: "absolute",
                            }}
                          >
                            <span
                              className="like-emoji"
                              style={{
                                fontSize: "18px",
                                display: "inline-block",
                                visibility: "visible",
                                opacity: 1,
                                zIndex: 0,
                                position: "relative",
                                color: "#FFD700",
                                lineHeight: 1,
                              }}
                            >
                              👍
                            </span>
                          </div>
                        )}
                        {reactionSummary &&
                          Object.keys(reactionSummary).length > 0 && (
                            <div
                              className="message-reactions"
                              style={{
                                display: "flex",
                                gap: "6px",
                                marginTop: "4px",
                              }}
                            >
                              {Object.entries(reactionSummary)
                                .filter(
                                  ([emoji, users]) =>
                                    emoji !== "like" && users.length > 0,
                                )
                                .map(([emoji, users]) => (
                                  <span
                                    key={emoji}
                                    style={{
                                      padding: "2px 6px",
                                      borderRadius: "12px",
                                      fontSize: "14px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    {emoji} {users.length}
                                  </span>
                                ))}
                            </div>
                          )}
                      </div>
                      {/* {replyCount > 0 && replyHtml && (
                    <div className="reply-container">
                      <span className="fst-italic fw-light"> Reply :</span>
                      <div
                        dangerouslySetInnerHTML={{ __html: replyHtml }}
                        style={{ flex: 1, wordBreak: "break-word" }}
                      />
                    </div>
                  )} */}
                    </>
                  )}
                  {/* </div>
                   */}
                </>
              ) : (
                // START MODIFIED TEXT/JOURNAL MESSAGE BLOCK
                <div
                  // Apply conditional class only when there is actual text content
                  // to show. If the message is just a pasted video link, skip the
                  // blue bubble entirely so the embed looks like an uploaded video.
                  className={`message-with-like-container ${
                    isJournalShared || (hasStandaloneEmbeds && !hasCleanedText)
                      ? ""
                      : "my-text-chat"
                  }`}
                  style={{
                    display: "flex",
                    alignItems:
                      hasStandaloneEmbeds && !hasCleanedText
                        ? "flex-start"
                        : "flex-end",
                    gap: "8px",
                    flexWrap: "wrap",
                    position: "relative",
                    flexDirection: "column",
                  }}
                >
                  {hasStandaloneEmbeds && renderChatEmbeds()}

                  {/* Message Content */}
                  {hasCleanedText && (
                    <div style={{ flex: 1, wordBreak: "break-word" }}>
                      {parseLinkifiedHtmlWithVideoEmbeds(cleanedTextHtml, {
                        keepLinkAnchorsInline: true,
                      })}
                    </div>
                  )}

                  {/* Journal Sharing UI - COPIED FROM SECOND FILE */}
                  {isJournalShared && (
                    <div style={{ width: "100%", marginTop: "8px" }}>
                      {/* Static text */}
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "400",
                          color: "#969FA7",
                          margin: "0 0 8px 0",
                        }}
                      >
                        User shared their {journal.emotion_name} reframe. Click
                        below to view detail.
                      </p>

                      {/* Journal preview box */}
                      <div
                        onClick={handleJournalClick}
                        style={{
                          backgroundColor: "#008CFF",
                          border: "2px solid white",
                          borderRadius: "14px",
                          padding: "12px 14px",
                          cursor: "pointer",
                          transition: "opacity 0.2s ease",
                        }}
                        onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
                        onMouseLeave={(e) => (e.target.style.opacity = "1")}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexDirection: "row",
                          }}
                        >
                          {/* Keywords */}
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              color: "#ffffff",
                              marginBottom: "8px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            {/* {formatKeywords(journal.journal_keywords)} */}
                            {/* {journal?.title_answer} */}
                            {/* {parse(journal?.title_answer)} */}
                            {journal?.title_answer
                              ? parse(journal.title_answer)
                              : "NA"}
                            {/* <span style={{
                              fontSize: "14px",
                              fontWeight: "400",
                              color: "#ffffff"
                            }}>
                              Description
                            </span> */}
                          </div>

                          <img
                            alt="arrow-icon"
                            src="/icons/arrow-square-right.svg"
                            style={{ width: "18px", height: "18px" }}
                          />
                        </div>
                      </div>

                      {/* Reply count link for journal messages */}
                      {replyCount > 0 && (
                        <Link
                          to={`/threads?conversationId=${conversationId + "_" + messageId}&messageId=${messageId}`}
                          className="reply-link"
                          style={{ marginTop: "8px", display: "inline-block" }}
                        >
                          {replyCount} Replies
                        </Link>
                      )}
                    </div>
                  )}
                  {/* END JOURNAL SHARING UI */}

                  {/* Reply count for non-journal messages */}
                  {!isJournalShared && replyCount > 0 && (
                    <Link
                      to={`/threads?conversationId=${
                        conversationId + "_" + messageId
                      }&messageId=${messageId}`}
                      className="reply-link"
                    >
                      {replyCount} Replies
                    </Link>
                  )}
                  {isLiked && !isEditing && (
                    <div
                      className="like-emoji-container mt-1"
                      style={{
                        textAlign: "right",
                        left: "14px",
                        bottom: "-14px",
                        position: "absolute",
                      }}
                    >
                      <span
                        className="like-emoji"
                        style={{
                          fontSize: "18px",
                          display: "inline-block",
                          visibility: "visible",
                          opacity: 1,
                          zIndex: 0,
                          position: "relative",
                          color: "#FFD700",
                          lineHeight: 1,
                        }}
                      >
                        👍
                      </span>
                    </div>
                  )}
                  {reactionSummary &&
                    Object.keys(reactionSummary).length > 0 && (
                      <div
                        className="message-reactions"
                        style={{
                          display: "flex",
                          gap: "6px",
                          marginTop: "4px",
                        }}
                      >
                        {Object.entries(reactionSummary)
                          .filter(
                            ([emoji, users]) =>
                              emoji !== "like" && users.length > 0,
                          )
                          .map(([emoji, users]) => (
                            <span
                              key={emoji}
                              style={{
                                padding: "2px 6px",
                                borderRadius: "12px",
                                fontSize: "14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {emoji} {users.length}
                            </span>
                          ))}
                      </div>
                    )}
                </div>
              )}

              {/* Action buttons */}

              {/* Action buttons */}
              {!isEditing && (
                <div className="message-actions actions-bar mt-2 gap-2">
                  <img
                    className="action-icon"
                    src="/icons/like-icon.svg"
                    onClick={handleLike}
                    alt="Like"
                    title="Like"
                  />
                  {showReactions && (
                    <div
                      ref={reactionsRef}
                      style={{
                        position: "absolute",
                        top: "-40px", // below the Like button
                        left: "-20px", // align left
                        display: "flex",
                        gap: "2px",
                        marginTop: "4px",
                        background: "#fff", // optional styling
                        padding: "4px 6px",
                        borderRadius: "6px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        zIndex: 10,
                        overflowX: "auto", // vertical scroll if needed
                        maxWidth: "200px", // limit width
                        // scrollbarWidth: "none"
                      }}
                    >
                      {reactions.map((emoji, index) => (
                        <button
                          key={index}
                          onClick={() => handleReactionSelect(emoji)}
                          style={{
                            fontSize: "20px",
                            cursor: "pointer",
                            background: "transparent",
                            border: "none",
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                  <img
                    className="action-icon"
                    src="/icons/chat-edit.svg"
                    onClick={handleEdit}
                    alt="Edit"
                    title="Edit"
                  />
                  <img
                    className="action-icon"
                    src="/icons/delete-icon.svg"
                    onClick={handleDelete}
                    alt="Delete"
                    title="Delete"
                  />
                  {showReply && (
                    <img
                      className="action-icon ml-2"
                      src="/icons/reply-icon.svg"
                      onClick={handleReply}
                      alt="Reply"
                      title="Reply"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {replyCount > 0 && (replyHtml || replyMessage?.file) && (
            <div className="reply-container mt-2">
              <span className="fst-italic fw-light">Reply:</span>

              {/* Text reply */}
              {replyHtml && (
                <div
                  style={{
                    flex: 1,
                    wordBreak: "break-word",
                    textOverflow: "ellipsis",
                  }}
                >
                  {parseLinkifiedHtmlWithVideoEmbeds(replyHtml)}
                </div>
              )}
              {/* File reply */}
              {replyMessage?.file?.type === "video" &&
                replyMessage?.file?.url && (
                  <div className="mt-2">
                    <ChatVideoPlayer videoUrl={replyMessage?.file.url} />
                  </div>
                )}

              {replyMessage?.file?.type === "audio" &&
                replyMessage?.file?.url && (
                  <div className="mt-2">
                    <ChatAudioPlayer src={replyMessage?.file.url} />
                  </div>
                )}

              {replyMessage?.file?.type === "document" &&
                replyMessage?.file?.url && (
                  <div className="mt-2">
                    <a
                      href={replyMessage?.file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#b3ddff" }}
                      className="text-decoration-none"
                    >
                      <span role="img" aria-label="document">
                        📄
                      </span>
                    </a>
                  </div>
                )}

              {replyMessage?.file?.type === "image" &&
                replyMessage?.file?.url && (
                  <div className="mt-2">
                    <div className="image-preview">
                      <img
                        src={replyMessage?.file?.url}
                        alt={replyMessage?.file?.name || "file preview"}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "200px",
                          borderRadius: "8px",
                          objectFit: "contain",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          onDownload();
                        }}
                      />
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
        <div className="ms-md-3 ms-1">
          <img
            src={userImage}
            alt="User Profile"
            className="user-profile-chat-image"
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          aria-labelledby="deleteMessageModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{ backgroundColor: "#132534" }}
            >
              <div className="modal-header position-relative d-flex justify-content-center">
                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute"
                  style={{
                    top: "30px",
                    right: "47px",
                    color: "#e6ecf1",
                  }}
                  onClick={handleCancelDelete}
                ></button>
                <h1
                  className="modal-title"
                  style={{ color: "#e6ecf1", fontSize: "24px" }}
                >
                  Delete Message
                </h1>
              </div>
              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-center"
                  style={{ color: "#e6ecf1", fontSize: "18px" }}
                >
                  Are you sure you want to delete this message?
                </p>

                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", background: "#db4545" }}
                    onClick={handleConfirmDelete}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px" }}
                    onClick={handleCancelDelete}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Journal Detail Modal */}
      <JournalDetailModal
        isOpen={showJournalModal}
        onClose={() => setShowJournalModal(false)}
        journal={journal}
        originalSender={{
          id: userId,
          name: responder,
          image: userImage,
        }}
        messageId={messageId}
        conversationId={conversationId}
        originalMessage={{
          content: response,
          senderId: userId,
          senderName: responder,
          senderProfileImage: userImage,
          journal: journal,
          isFile: isFile,
          file: fileInfo,
        }}
      />
    </>
  );
};

export default ReceiveChat;
